import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { useReviewView } from '@/hooks/use-review-view'

export type BadgeKind =
    | 'core'
    | 'alt'
    | 'extrapolated'
    | 'draft'
    | 'needs_review'
    | 'verified'
    | 'published'
    | 'archived'

const LABELS: Record<BadgeKind, { hi: string, en: string, className: string }> = {
    core: { hi: 'मुख्य', en: 'core', className: 'bg-secondary text-foreground border-border' },
    alt: { hi: 'विकल्प', en: 'variant', className: 'bg-transparent text-terracotta border-terracotta/50' },
    extrapolated: { hi: 'अनुमानित', en: 'extrapolated', className: 'bg-transparent text-muted-foreground border-dashed border-border' },
    draft: { hi: 'प्रारूप', en: 'draft', className: 'bg-secondary text-muted-foreground border-border' },
    needs_review: { hi: 'समीक्षाधीन', en: 'awaiting review', className: 'bg-saffron/12 text-saffron-strong border-saffron/40' },
    verified: { hi: 'सत्यापित', en: 'verified', className: 'bg-success-soft text-success border-success/40' },
    published: { hi: 'प्रकाशित', en: 'published', className: 'bg-success text-white border-success dark:text-background' },
    archived: { hi: 'संग्रहीत', en: 'archived', className: 'bg-transparent text-muted-foreground border-border line-through' }
}

interface StatusBadgeProps {
    kind: BadgeKind
    /** Show the English word too (default: Hindi with English in the tooltip text). */
    withEnglish?: boolean
    className?: string
}

/**
 * Review states (and the extrapolated marker) are for the people who review content, never for learners: they show
 * in /admin, and on learner pages only when an editor, reviewer or admin has switched on "Show review labels".
 * core and alt (विकल्प) are grammar labels and show for everyone.
 */
const REVIEW_KINDS = new Set<BadgeKind>(['extrapolated', 'draft', 'needs_review', 'verified', 'published', 'archived'])

export const StatusBadge: FC<StatusBadgeProps> = ({ kind, withEnglish = true, className }): ReactNode => {
    const reviewView = useReviewView()
    const label = LABELS[kind]

    // Learners (and the server render, before the session is known) never see review states.
    if (REVIEW_KINDS.has(kind) && !reviewView) return null

    return (
        <span
            title={`${label.hi} · ${label.en}`}
            className={cn(
                'inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[0.6875rem] font-semibold leading-none whitespace-nowrap',
                label.className,
                className
            )}
        >
            <span lang='hi' className='hindi'>{label.hi}</span>
            {withEnglish && <span lang='en' className='font-medium opacity-80'>{label.en}</span>}
        </span>
    )
}

export const reviewBadgeKind = (status: string | undefined | null): BadgeKind => {
    if (status === 'verified' || status === 'published' || status === 'draft' || status === 'archived') return status

    return 'needs_review'
}

/**
 * Renders its content only in review view (see useReviewView): editors, reviewers and admins inside /admin, or on
 * learner pages with "Show review labels" on. Review notes are never shown to learners.
 */
export const ReviewerOnly: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const reviewView = useReviewView()

    return reviewView ? children : null
}
